import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as signalR from '@microsoft/signalr';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useAppData } from './AppDataContext';
import { useCustomerStoreName } from './StoreContext';
import { API_URL } from '../config/runtimeConfig';
import { getStoreId, isSubdomainStoreMode } from '../utils/storeUtils';
import { Permissions } from '../constants/Permissions';
import { usePermissions } from '../hooks/usePermissions';
import {
  getSupportGuestToken,
  getSupportWaitingCount,
  getVisitorSupportConversation,
  sendVisitorSupportMessage,
} from '../services/supportChatService';
import SupportChatPanel from '../components/common/SupportChatPanel';
import {
  messagesAfterVisitorSend,
  resolveSupportChatHubAccessToken,
  sameSupportId,
  shouldOpenSupportFromLocation,
  shouldShowGuestEmailField,
  createSupportTypingReporter,
  isVisitorWidgetPeerTyping,
} from '../utils/supportChatThread';

const SupportChatContext = createContext(null);

const isAppAdminUser = (user) => {
  const userType = user?.userType;
  return userType === 1 || userType === '1' || userType === 'AppAdmin' || userType === 'appAdmin';
};

const resolveHubUrl = (guestToken) => {
  const apiBase = (API_URL || '').replace(/\/$/, '');
  const origin = apiBase.replace(/\/api$/i, '');
  const url = `${origin}/hubs/support-chat`;
  return guestToken ? `${url}?guest_token=${encodeURIComponent(guestToken)}` : url;
};

const resolveSource = () => {
  const path = window.location.pathname;
  if (path.startsWith('/management')) {
    return 'StaffWeb';
  }
  if (
    path.includes('/login')
    || path.includes('/register')
    || path.includes('/reset-password')
    || path.includes('/set-password')
  ) {
    return 'Auth';
  }
  return 'CustomerWeb';
};

const resolveVisitorAuthContext = () => {
  const path = window.location.pathname;
  if (isSubdomainStoreMode() || path.startsWith('/stores/')) {
    return 'customer';
  }
  if (path.startsWith('/management')) {
    return 'management';
  }
  if (localStorage.getItem('customer_auth_token')) {
    return 'customer';
  }
  if (localStorage.getItem('management_auth_token')) {
    return 'management';
  }
  return null;
};

export const SupportChatProvider = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const {
    isCustomerAuthenticated,
    isManagementAuthenticated,
    managementUser,
    customerLoading,
    managementLoading,
  } = useAuth();

  const { currentStore } = useAppData();
  const customerStoreName = useCustomerStoreName();
  const { hasPermission } = usePermissions(currentStore?.id);

  const isCustomerSurface = isSubdomainStoreMode() || location.pathname.startsWith('/stores/');
  const isAppAdmin = isAppAdminUser(managementUser) && !isCustomerSurface;
  const isStaffWidget = isManagementAuthenticated && !isAppAdmin && !isCustomerSurface;
  const storeId = getStoreId(location.pathname, location.search) || currentStore?.id || null;
  const isStoreChat = isCustomerSurface && Boolean(storeId);
  const showVisitorWidget = !isAppAdmin && (isStaffWidget || isStoreChat);
  const canSeeInbox = isAppAdmin || (!isCustomerSurface && hasPermission(Permissions.Chat));
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [status, setStatus] = useState(null);
  const [draft, setDraft] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [waitingCount, setWaitingCount] = useState(0);
  const [hasGuestToken, setHasGuestToken] = useState(() => Boolean(getSupportGuestToken()));
  const [peerTyping, setPeerTyping] = useState(false);
  const connectionRef = useRef(null);
  const conversationIdRef = useRef(null);
  const sendTypingRef = useRef(() => undefined);
  const typingReporterRef = useRef(null);
  const peerTypingTimeoutRef = useRef(null);

  conversationIdRef.current = conversationId;

  if (!typingReporterRef.current) {
    typingReporterRef.current = createSupportTypingReporter({
      send: (isTyping) => sendTypingRef.current(isTyping),
    });
  }

  const visitorAuthenticated = isCustomerAuthenticated || (isManagementAuthenticated && !isAppAdmin);
  const showGuestEmail = shouldShowGuestEmailField(visitorAuthenticated, hasGuestToken);

  const sendTyping = useCallback((isTyping) => {
    const connection = connectionRef.current;
    const id = conversationIdRef.current;
    if (!connection || !id || connection.state !== signalR.HubConnectionState.Connected) {
      return;
    }
    connection.invoke('NotifyTyping', id, isTyping, getSupportGuestToken()).catch(() => undefined);
  }, []);
  sendTypingRef.current = sendTyping;

  const clearPeerTyping = useCallback(() => {
    if (peerTypingTimeoutRef.current) {
      clearTimeout(peerTypingTimeoutRef.current);
      peerTypingTimeoutRef.current = null;
    }
    setPeerTyping(false);
  }, []);

  const openedFromPushRef = useRef(false);

  const openChat = useCallback((message) => {
    if (isAppAdmin || !showVisitorWidget) {
      return;
    }
    if (typeof message === 'string' && message.trim()) {
      setDraft(message);
    }
    setOpen(true);
    getVisitorSupportConversation(resolveVisitorAuthContext(), isStoreChat ? storeId : null)
      .then((conversation) => {
        if (!conversation?.id) {
          return;
        }
        conversationIdRef.current = conversation.id;
        setConversationId(conversation.id);
        setStatus(conversation.status || null);
        setMessages(conversation.messages || []);
      })
      .catch(() => undefined);
  }, [isAppAdmin, showVisitorWidget, isStoreChat, storeId]);

  useEffect(() => {
    if (openedFromPushRef.current || !shouldOpenSupportFromLocation(location.search) || !showVisitorWidget) {
      return;
    }
    openedFromPushRef.current = true;
    openChat();
  }, [location.search, openChat, showVisitorWidget]);

  const closeChat = useCallback(() => setOpen(false), []);

  const upsertMessage = useCallback((incoming) => {
    if (!incoming?.id) {
      return;
    }
    setMessages((current) => {
      if (current.some((item) => sameSupportId(item.id, incoming.id))) {
        return current;
      }
      return [...current, incoming];
    });
  }, []);

  useEffect(() => {
    if (customerLoading || managementLoading || isAppAdmin) {
      return undefined;
    }

    let cancelled = false;
    const authContext = resolveVisitorAuthContext();

    getVisitorSupportConversation(authContext, isStoreChat ? storeId : null)
      .then((conversation) => {
        if (cancelled) {
          return;
        }
        setConversationId(conversation?.id || null);
        setStatus(conversation?.status || null);
        setMessages(conversation?.messages || []);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [customerLoading, managementLoading, isAppAdmin, isCustomerAuthenticated, isManagementAuthenticated, isStoreChat, storeId]);

  useEffect(() => {
    if (customerLoading || managementLoading) {
      return undefined;
    }

    const guestToken = getSupportGuestToken();
    const token = resolveSupportChatHubAccessToken({
      isCustomerSurface,
      isManagementSession: isAppAdmin || isManagementAuthenticated,
    });

    if (isAppAdmin && !token) {
      return undefined;
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(resolveHubUrl(guestToken), {
        accessTokenFactory: () => resolveSupportChatHubAccessToken({
          isCustomerSurface,
          isManagementSession: isAppAdmin || isManagementAuthenticated,
        }),
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .build();

    connection.on('ReceiveSupportMessage', (message) => {
      if (!conversationIdRef.current || sameSupportId(message?.conversationId, conversationIdRef.current)) {
        if (isVisitorWidgetPeerTyping(message?.senderKind, isStaffWidget)) {
          clearPeerTyping();
        }
        upsertMessage(message);
      }
    });

    connection.on('SupportConversationChanged', (change) => {
      if (canSeeInbox) {
        getSupportWaitingCount()
          .then(setWaitingCount)
          .catch(() => undefined);
      }
      if (change?.status && sameSupportId(change.conversationId, conversationIdRef.current)) {
        setStatus(change.status);
      }
    });

    connection.on('SupportTyping', (payload) => {
      if (!sameSupportId(payload?.conversationId, conversationIdRef.current)) {
        return;
      }
      if (!isVisitorWidgetPeerTyping(payload?.senderKind, isStaffWidget)) {
        return;
      }
      setPeerTyping(Boolean(payload?.isTyping));
      if (payload?.isTyping) {
        if (peerTypingTimeoutRef.current) {
          clearTimeout(peerTypingTimeoutRef.current);
        }
        peerTypingTimeoutRef.current = setTimeout(() => setPeerTyping(false), 5000);
      }
    });

    connectionRef.current = connection;
    connection.start()
      .then(async () => {
        if (isAppAdmin) {
          await connection.invoke('JoinInbox', null);
          const count = await getSupportWaitingCount();
          setWaitingCount(count);
        } else if (canSeeInbox && storeId) {
          await connection.invoke('JoinInbox', storeId);
          const count = await getSupportWaitingCount();
          setWaitingCount(count);
        }
        const activeId = conversationIdRef.current;
        if (activeId) {
          await connection.invoke('JoinConversation', activeId, getSupportGuestToken());
        }
      })
      .catch(() => undefined);

    return () => {
      connection.stop().catch(() => undefined);
      connectionRef.current = null;
    };
  }, [
    customerLoading,
    managementLoading,
    isAppAdmin,
    isStaffWidget,
    canSeeInbox,
    storeId,
    isManagementAuthenticated,
    isCustomerAuthenticated,
    isCustomerSurface,
    upsertMessage,
    clearPeerTyping,
  ]);

  useEffect(() => {
    const connection = connectionRef.current;
    if (!connection || !conversationId || connection.state !== signalR.HubConnectionState.Connected) {
      return;
    }
    connection.invoke('JoinConversation', conversationId, getSupportGuestToken()).catch(() => undefined);
  }, [conversationId]);

  const sendMessage = useCallback(async () => {
    const body = draft.trim();
    if (!body || sending) {
      return;
    }
    if (showGuestEmail && !guestEmail.trim()) {
      setError(t('supportChat.guestEmailRequired'));
      return;
    }

    setSending(true);
    setError(null);
    try {
      const result = await sendVisitorSupportMessage({
        body,
        guestEmail: showGuestEmail ? guestEmail.trim() : null,
        guestToken: getSupportGuestToken(),
        storeId: isStoreChat ? storeId : (getStoreId(window.location.pathname, window.location.search) || null),
        source: resolveSource(),
      }, resolveVisitorAuthContext());

      setDraft('');
      typingReporterRef.current?.stop();
      if (result.guestToken) {
        setHasGuestToken(true);
      }
      const previousConversationId = conversationIdRef.current;
      conversationIdRef.current = result.conversationId;
      setMessages((current) => messagesAfterVisitorSend(previousConversationId, current, result));
      setConversationId(result.conversationId);
      setStatus(result.status);
      if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
        connectionRef.current.invoke(
          'JoinConversation',
          result.conversationId,
          getSupportGuestToken(),
        ).catch(() => undefined);
      }
    } catch (sendError) {
      setError(sendError?.response?.data?.message || t('supportChat.sendFailed'));
    } finally {
      setSending(false);
    }
  }, [draft, sending, showGuestEmail, guestEmail, t, isStoreChat, storeId]);

  const handleDraftChange = useCallback((value) => {
    setDraft(value);
    if (!conversationIdRef.current || status === 'Closed') {
      return;
    }
    if (value.trim()) {
      typingReporterRef.current?.ping();
    } else {
      typingReporterRef.current?.stop();
    }
  }, [status]);

  const value = useMemo(() => ({
    openChat,
    closeChat,
    isAppAdmin,
    isStoreChat,
    waitingCount,
    isOpen: open,
    conversationId,
  }), [openChat, closeChat, isAppAdmin, isStoreChat, waitingCount, open, conversationId]);

  return (
    <SupportChatContext.Provider value={value}>
      {children}
      {showVisitorWidget ? (
        <SupportChatPanel
          open={open}
          onClose={closeChat}
          messages={messages}
          draft={draft}
          onDraftChange={handleDraftChange}
          onSend={sendMessage}
          sending={sending}
          guestEmail={guestEmail}
          onGuestEmailChange={setGuestEmail}
          showGuestEmail={showGuestEmail}
          status={status}
          error={error}
          dockAboveBottomNav={isCustomerSurface}
          peerTyping={peerTyping}
          isStaffWidget={isStaffWidget}
          isStoreChat={isStoreChat}
          storeName={customerStoreName}
        />
      ) : null}
    </SupportChatContext.Provider>
  );
};

export const useSupportChat = () => {
  const context = useContext(SupportChatContext);
  if (!context) {
    throw new Error('useSupportChat must be used within SupportChatProvider');
  }
  return context;
};
